import { UnauthorizedReason, AccessPermissions } from '../types.js';

/**
 * Is Authorized Result.
 * @param isAuthorized - Is the access authorized?
 * @param failureReason - Permission failure reason.
 * @param options - More options.
 */ function isAuthorized(isAuthorized, failureReason, options = {}) {
    return {
        isAuthorized,
        failureReason,
        ...options
    };
}
/**
 * Handle Access Permission.
 * @param access - Permission access.
 * @param authenticated - Is the user authenticated?
 * @param roles - Permission roles.
 */ function handleAccessPermissions(access, authenticated, roles = []) {
    // Unauthenticated regardless of authentication or roles.
    if (access === AccessPermissions.restricted) {
        return isAuthorized(false, UnauthorizedReason.Restricted);
    }
    // Check access permissions for authenticated user.
    if (authenticated) {
        switch(access){
            // The PublicOnly permission requires the user to be unauthenticated.
            case AccessPermissions.publicOnly:
                return isAuthorized(false, UnauthorizedReason.IsAuthenticated);
            // The ProtectedNoRoles permission requires the user to have empty roles.
            case AccessPermissions.protectedNoRoles:
                return roles.length > 0 ? isAuthorized(false, UnauthorizedReason.HasRoles) : isAuthorized(true);
            // The Protected permission necessitates user authentication irrespective of their role status.
            // The Public permission is authorized for users irrespective of their authentication status.
            case AccessPermissions.protected:
            case AccessPermissions.public:
                return isAuthorized(true);
        }
    }
    // Check access permission for unauthenticated user.
    switch(access){
        // Any Public permission is authorized for unauthenticated users.
        case AccessPermissions.public:
        case AccessPermissions.publicOnly:
            return isAuthorized(true);
        // Protected roles are for authenticated users only.
        case AccessPermissions.protected:
        case AccessPermissions.protectedNoRoles:
            return isAuthorized(false, UnauthorizedReason.NotAuthenticated);
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
        default:
            // For safety measures, in the scenario where an unknown access permission is accepted,
            // always default to unauthorized access.
            return isAuthorized(false, UnauthorizedReason.Unexpected);
    }
}
/**
 * Handle Dynamic Permissions.
 * @param permissions - Permission set.
 * @param roles - Permission roles.
 */ function handleDynamicPermissions(permissions, roles = []) {
    // If ANY excluded permissions are found, this fails and does not allow the action
    if (permissions.excludeAny) {
        const foundExcludedRoles = permissions.excludeAny.filter((role)=>roles.includes(role));
        // Return error if excluded roles are found
        if (foundExcludedRoles.length > 0) {
            return isAuthorized(false, UnauthorizedReason.HasRoles, {
                hasRoles: foundExcludedRoles
            });
        }
    }
    // If missing permission roles are found, this fails and does not allow the action
    let missingRoles = [];
    // All `includeAll` roles need to match;
    // missing roles will be added to missingPermissionRoles array
    if (permissions.includeAll) {
        missingRoles = [
            ...missingRoles,
            ...permissions.includeAll.filter((role)=>!roles.includes(role))
        ];
    }
    // Any `includeAny` role can match;
    // if both are missing, they are added to the missingPermissionRoles array
    if (!permissions.includeAny?.some((role)=>roles.includes(role))) {
        missingRoles = [
            ...missingRoles,
            ...permissions.includeAny ? permissions.includeAny : []
        ];
    }
    // Return error if missing role is found
    if (missingRoles.length > 0) {
        return isAuthorized(false, UnauthorizedReason.MissingRoles, {
            missingRoles
        });
    }
    // If a permission set matches roles, authorize the user
    return isAuthorized(true);
}
/**
 * Permission Check.
 * @param permissionsSet - PermissionSet.
 * @throws Error for missing permission.
 */ function permissionCheck(permissionsSet) {
    /**
     * Plugins are optional and executed by the order they are set.
     * @param plugins - Permissions plugins to check permission based on additional logic.
     */ function plugins(plugins = []) {
        /**
         * Indicate if the user is authenticated.
         * @param authenticated - Boolean.
         */ function authenticated(authenticated) {
            /**
             * Verify permissions.
             * @param roles - String[].
             */ function verify(roles = []) {
                // Always fallback to empty roles
                if (roles == null) {
                    roles = [];
                }
                // Iterate all the plugins first.
                for (const pluginFn of plugins){
                    // Run each plugin with provided options,
                    // so each plugin can use them to run.
                    const check = pluginFn.verify({
                        authenticated,
                        permissionsSet
                    });
                    // Check if the plugin unauthorized
                    if (check?.isAuthorized === false) {
                        // And return the result only for unauthorized
                        return check;
                    }
                }
                // Check for missing permissions.
                if (permissionsSet == null) {
                    return isAuthorized(false, UnauthorizedReason.MissingPermissions);
                }
                // By default, access permission is required;
                // therefore, we must first check it before moving on to verify the roles.
                const accessPermission = handleAccessPermissions(permissionsSet.access, authenticated, roles);
                // When access permission is not authorized, we can halt here.
                // There is no need to proceed with further checks.
                if (!accessPermission.isAuthorized) {
                    return accessPermission;
                }
                // Once the access permission is authorized, we can then proceed to check the roles.
                return handleDynamicPermissions(permissionsSet, roles);
            }
            return {
                verify
            };
        }
        return {
            authenticated
        };
    }
    return {
        plugins
    };
}

export { permissionCheck };
//# sourceMappingURL=check.js.map
